import React from 'react';
import { getPaytableLabel } from '../config';

// Pure UI for the slot experience. All state/logic is pushed in via props so AI and humans
// can reason about layout separately from game math and PIXI.
function SlotLayout({
  stageRef,
  baseUrl,
  balance,
  bet,
  betPickerOpen,
  onOpenBetPicker,
  onCloseBetPicker,
  onSelectBet,
  onChangeBet,
  betOptions,
  autoOpen,
  onOpenAuto,
  onCloseAuto,
  onStartAuto,
  autoRounds,
  onSetAutoRounds,
  autoRemaining,
  menuOpen,
  onOpenMenu,
  onCloseMenu,
  menuIcon,
  onOpenPaytable,
  paytableData,
  paytableOpen,
  onClosePaytable,
  paylines,
  devOpen,
  status,
  onLogWindow,
  devForceLine,
  onSetDevForceLine,
  onSetDevForceWrapper,
  onForceWin,
  devShowLines,
  onTogglePaylines,
  onShowWinFrame,
  lastWin,
  winHighlights,
  winDisplay,
  formatAmount,
  handleSpin,
  isSpinning,
  soundOn,
  toggleSound,
  spinIcon,
  autoSpinIcon,
  soundOnIcon,
  soundOffIcon,
  autospinRounds,
}) {
  return (
    <div
      className="shell"
      style={{ '--stage-bg': `url(${baseUrl}img/grandtheftautobackground.jpg)` }}
    >
      <button className="menu-fab" onClick={onOpenMenu} aria-label="Menu">
        <img src={menuIcon} alt="Menu" />
      </button>
      {winDisplay && winDisplay.length > 0 && (
        <div className="win-overlay">
          {winDisplay.map((w, idx) => {
            if (!w.coords || !w.coords.length) return null;
            const first = w.coords[0];
            const last = w.coords[w.coords.length - 1];
            // Position above the midpoint of the matched coords.
            const midCol = (first.col + last.col) / 2;
            const midRow = (first.row + last.row) / 2;
            const left = `${(midCol + 0.5) / 5 * 100}%`;
            const top = `${(midRow + 0.5) / 5 * 100}%`;
            return (
              <div
                key={`${w.line}-${idx}`}
                className="win-card"
                style={{ left, top }}
              >
                <div className="win-label">{w.label || getPaytableLabel(w.key)}</div>
                <div className="win-amount">
                  {formatAmount ? formatAmount(w.current) : w.current.toFixed(2)}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="hud" />
      <div className="stage" ref={stageRef} />
      <div className="footer">
        <div className="pill pill-metric footer-left">
          <span className="pill-label">Credits</span>
          <span className="pill-value">{balance}</span>
        </div>
        <div className="footer-right">
          <div className="pill pill-metric bet-pill" onClick={onOpenBetPicker}>
            <span className="pill-label">Set Amount</span>
            <span className="pill-value">{bet}</span>
          </div>
        </div>
        <div className="footer-disclaimer">
          Not real money. No prizes. No in-app purchases. 18+.
        </div>
      </div>
      {devOpen && (
        <div className="dev-panel">
          <div className="dev-title">Debug Panel (press “D” to toggle)</div>
          <div className="dev-row">
            <span>Status:</span>
            <span className="dev-chip">{status}</span>
          </div>
          <div className="dev-row">
            <button className="dev-btn" onClick={onLogWindow}>
              Log window symbols
            </button>
          </div>
          <div className="dev-row">
            <label>Force line</label>
            <select value={devForceLine} onChange={(e) => onSetDevForceLine(Number(e.target.value))}>
              {paylines.map((_, idx) => (
                <option key={idx} value={idx}>
                  Line {idx + 1}
                </option>
              ))}
            </select>
          </div>
          <button className="dev-btn" onClick={onForceWin}>
            Force win next spin
          </button>
          <button className="dev-btn" onClick={() => onSetDevForceWrapper(true)}>
            Force wrapper next spin
          </button>
          <button className="dev-btn" onClick={onShowWinFrame}>
            Show win frame
          </button>
          <div className="dev-row">
            <span>Paylines overlay</span>
            <button className="dev-btn" onClick={onTogglePaylines}>
              {devShowLines ? 'Hide paylines' : 'Show paylines'}
            </button>
          </div>
          {lastWin && (
            <div className="dev-last">
              <div>Last win: {lastWin.amount} @ {lastWin.time}</div>
              <div>Lines: {lastWin.lines.map((l) => l + 1).join(', ')}</div>
            </div>
          )}
        </div>
      )}
      <div className="side-actions">
        <button
          className="auto-fab"
          onClick={onOpenAuto}
          aria-label="Autoplay"
        >
          <img src={autoSpinIcon} alt="Autoplay" />
          {autoRemaining > 0 && <div className="fab-badge">{autoRemaining}</div>}
        </button>
        <button
          className="spin-fab"
          onClick={() => handleSpin()}
          disabled={isSpinning || balance < bet}
          aria-label="Spin"
        >
          <img src={spinIcon} alt="Spin" />
        </button>
      </div>
      <button
        className="sound-fab"
        onClick={toggleSound}
        aria-label={soundOn ? 'Sound on' : 'Sound off'}
      >
        <img src={soundOn ? soundOnIcon : soundOffIcon} alt="Sound toggle" />
      </button>
      {betPickerOpen && (
        <div className="bet-overlay" onClick={onCloseBetPicker}>
          <div className="bet-panel" onClick={(e) => e.stopPropagation()}>
            <div className="bet-panel-title">Set Amount</div>
            <div className="bet-grid">
              {betOptions.map((opt) => (
                <button
                  key={opt}
                  className={`bet-option ${opt === bet ? 'active' : ''}`}
                  onClick={() => onSelectBet(opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
            <button className="bet-close" onClick={onCloseBetPicker}>
              Close
            </button>
          </div>
        </div>
      )}
      {autoOpen && (
        <div className="bet-overlay" onClick={onCloseAuto}>
          <div className="bet-panel" onClick={(e) => e.stopPropagation()}>
            <div className="bet-panel-title">Autoplay</div>
            <div className="auto-row">
              <span className="pill-label">Rounds</span>
              <div className="bet-grid">
                {autospinRounds.map((n) => (
                  <button
                    key={n}
                    className={`bet-option ${n === autoRounds ? 'active' : ''}`}
                    onClick={() => onSetAutoRounds(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="auto-row controls-inline">
              <span className="pill-label">Set Amount per round</span>
              <div className="controls">
                <button onClick={() => onChangeBet(Math.max(1, bet - 1))} disabled={isSpinning}>-</button>
                <div className="pill pill-metric compact">
                  <span className="pill-value">{bet}</span>
                </div>
                <button onClick={() => onChangeBet(bet + 1)} disabled={isSpinning}>+</button>
              </div>
            </div>
            <div className="auto-actions">
              <button className="bet-close" onClick={onCloseAuto}>
                Cancel
              </button>
              <button
                className="bet-option start"
                disabled={!autoRounds || isSpinning || balance < bet}
                onClick={onStartAuto}
              >
                Start
              </button>
            </div>
          </div>
        </div>
      )}
      {menuOpen && (
        <div className="menu-dropdown">
          <div className="menu-actions">
            <button className="menu-btn" onClick={onOpenPaytable}>
              Win Combos
            </button>
            <button className="menu-btn ghost" onClick={onCloseMenu}>
              Close
            </button>
          </div>
        </div>
      )}

      {paytableOpen && (
        <div className="bet-overlay" onClick={onClosePaytable}>
          <div className="paytable-panel">
            <div className="bet-panel-title">Win Combos</div>
            <div className="paytable-grid">
              {paytableData.map((item) => (
                <div key={item.id} className="paytable-card">
                  <div className="paytable-thumb">
                    <img src={`${baseUrl}img/${item.file}`} alt={item.id} />
                  </div>
                  <div className="paytable-info">
                    <div className="paytable-label">{getPaytableLabel(item.id)}</div>
                    {item.payouts ? (
                      <div className="paytable-payouts">
                        {['5', '4', '3'].map((k) =>
                          item.payouts[k] ? (
                            <div key={k} className="payout-row">
                              <span className="payout-count">{k}x</span>
                              <span className="payout-value">{item.payouts[k]}</span>
                            </div>
                          ) : null
                        )}
                      </div>
                    ) : (
                      <div className="paytable-note">Scatter / Special</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button className="bet-close" onClick={onClosePaytable}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SlotLayout;
